import type { TransactionLineItem } from 'webReservations/checkout/apiHelpers';
import { Restaurant } from 'webReservations/reservations/apiHelpers';
import { FloorPlanData } from '@shared/types/floorPlans';
import { ReservationStatus } from '@shared/types/reservations';
import { RestaurantEvent } from '@shared/types/restaurants';
import { api } from '../api';

export interface CancellationPolicy {
  applicableAt: string;
  fee: number;
}

export interface ReservationWithDetails {
  buyNowPrice: number | null;
  date: string;
  floorPlanSnapshotHash: string | null;
  guestCount: number;
  id: string;
  isCancelableByGuest: boolean;
  canBeResold: boolean;
  floorPlan: FloorPlanData | null;
  note: string | null;
  restaurant: Restaurant;
  restaurantEvent: Pick<
    RestaurantEvent,
    'id' | 'name' | 'location' | 'directions'
  > | null;
  soldForPrice: number | null;
  status: ReservationStatus;
  listing: {
    id: string;
    publicName: string;
  } | null;
  time: string;
  transaction: {
    fee: number;
    paymentMethod: string;
    price: number;
    purchaseDate: string; // ISO 8601 date format
    refundAmount?: number;
    tax: number;
    receiptNumber: string;
    cancellationPolicy: CancellationPolicy | null;
    transactionLineItems: TransactionLineItem[];
    total: number;
    purchaseDiscount?: {
      code: string | null;
      percentage: number;
      amount: number;
    } | null;
  };
  wasCreatedFromAcceptedOffer: boolean;
  wasTableChosenAtTimeOfBooking: boolean;
}

export type TopOffer =
  | {
      expiredAt: string; // ISO 8601 date format
      id: string;
      price: number;
    }
  | Record<string, never>;

export const getReservation = async (
  reservationId: string,
): Promise<ReservationWithDetails> => api.get(`/reservations/${reservationId}`);

export const getTopOffer = async (
  reservationId: string,
  signal?: AbortSignal,
): Promise<TopOffer> =>
  api.get(`/reservations/${reservationId}/top-offer`, {
    signal,
  });

export const cancelReservation = async (reservationId: string): Promise<void> =>
  api.post(`/reservations/${reservationId}/cancel`);
